/**
 * @file Missions quotidiennes/hebdo/succes, passe de saison (25 paliers, piste gratuite + epic), cadeaux de connexion sur 7 jours.
 */
import type { DailyRewardDay, MissionDef, PassConfig, PassTier, RewardBundle } from './types';

const m = (
  id: string,
  kind: MissionDef['kind'],
  metric: MissionDef['metric'],
  target: number,
  reward: RewardBundle,
  fr: string,
  en: string,
  scaleWithStage = false,
): MissionDef => ({ id, kind, metric, target, reward, scaleWithStage, name: { fr, en } });

/** `{n}` dans les noms est remplace par l'objectif (i18n cote UI). Les cibles "scaleWithStage" sont recalculees : voir BALANCING.md §Missions. */
export const MISSIONS: readonly MissionDef[] = [
  // ---- Quotidiennes (6 tirees parmi ces modeles) ----
  m('d_trees', 'daily', 'treeKills', 100, { wallet: { dia: 20 }, passXp: 20 }, 'Abattre {n} arbres', 'Chop {n} trees', true),
  m('d_stages', 'daily', 'stageClears', 5, { wallet: { dia: 30 }, passXp: 20 }, 'Reussir {n} niveaux', 'Clear {n} levels'),
  m('d_upgrades', 'daily', 'upgradesBought', 50, { wallet: { dia: 15 }, passXp: 20 }, 'Acheter {n} ameliorations', 'Buy {n} upgrades'),
  m('d_draws', 'daily', 'gunDraws', 3, { wallet: { dia: 20 }, passXp: 20 }, 'Tirer {n} armes', 'Draw {n} weapons'),
  m('d_merges', 'daily', 'gunMerges', 2, { wallet: { dia: 20 }, passXp: 20 }, 'Fusionner {n} armes', 'Merge {n} weapons'),
  m('d_waves', 'daily', 'wavesCleared', 10, { wallet: { dia: 25, petCoin: 20 }, passXp: 20 }, 'Repousser {n} vagues', 'Survive {n} waves'),
  m('d_monsters', 'daily', 'monsterKills', 80, { wallet: { dia: 20 }, passXp: 20 }, 'Vaincre {n} monstres', 'Defeat {n} monsters', true),
  m('d_money', 'daily', 'moneyEarned', 1e6, { wallet: { dia: 25 }, passXp: 20 }, 'Gagner {n} argent', 'Earn {n} money', true),
  // ---- Hebdomadaires (3 tirees) ----
  m('w_trees', 'weekly', 'treeKills', 1000, { wallet: { dia: 120 }, passXp: 100 }, 'Abattre {n} arbres', 'Chop {n} trees', true),
  m('w_stages', 'weekly', 'stageClears', 40, { wallet: { dia: 150 }, passXp: 100 }, 'Reussir {n} niveaux', 'Clear {n} levels'),
  m('w_rebirth', 'weekly', 'rebirths', 2, { wallet: { dia: 150, ruby: 10 }, passXp: 100 }, 'Faire {n} Rebirth', 'Rebirth {n} times'),
  m('w_boss', 'weekly', 'bossKills', 8, { wallet: { dia: 120 }, passXp: 100 }, 'Vaincre {n} boss', 'Defeat {n} bosses'),
  // ---- Succes (permanents, un seul palier chacun ; paliers suivants = nouvelles entrees) ----
  m('a_trees_1', 'achievement', 'treeKills', 500, { wallet: { dia: 50 } }, 'Bucheron : {n} arbres', 'Lumberjack: {n} trees'),
  m('a_trees_2', 'achievement', 'treeKills', 10000, { wallet: { dia: 200 } }, 'Maitre bucheron : {n} arbres', 'Master lumberjack: {n} trees'),
  m('a_stage_1', 'achievement', 'stageClears', 50, { wallet: { dia: 100 } }, 'Explorateur : {n} niveaux', 'Explorer: {n} levels'),
  m('a_stage_2', 'achievement', 'stageClears', 500, { wallet: { dia: 500 } }, 'Conquerant : {n} niveaux', 'Conqueror: {n} levels'),
  m('a_rebirth_1', 'achievement', 'rebirths', 1, { wallet: { dia: 100 } }, 'Premiere renaissance', 'First rebirth'),
  m('a_merge_1', 'achievement', 'gunMerges', 25, { wallet: { dia: 100 } }, 'Forgeron : {n} fusions', 'Smith: {n} merges'),
];

const FREE_REWARD = (tier: number): RewardBundle => {
  if (tier % 10 === 0) return { draws: [{ pool: 'normal', count: 3 }], wallet: { dia: 60 } };
  if (tier % 5 === 0) return { draws: [{ pool: 'normal', count: 1 }], wallet: { dia: 30 } };
  return { wallet: { dia: 10 + tier } };
};
const EPIC_REWARD = (tier: number): RewardBundle => {
  if (tier === 25) return { draws: [{ pool: 'epic', count: 2 }], wallet: { dia: 300, ruby: 20 } };
  if (tier % 5 === 0) return { draws: [{ pool: 'epic', count: 1 }], wallet: { dia: 100 }, buffs: [{ id: 'moneyBuff', seconds: 300 }] };
  return { wallet: { dia: 25 + 2 * tier }, buffs: tier % 2 === 0 ? [{ id: 'powerBuff', seconds: 300 }] : undefined };
};

const XP_PER_TIER = 100;
const TIERS: PassTier[] = Array.from({ length: 25 }, (_, i) => ({
  tier: i + 1,
  xpRequired: XP_PER_TIER * (i + 1),
  free: FREE_REWARD(i + 1),
  epic: EPIC_REWARD(i + 1),
}));

export const PASS: PassConfig = {
  seasonDays: 28,
  tierCount: 25,
  xpPerTier: XP_PER_TIER,
  // La piste "Epic" se debloque avec des gemmes (pas d'achat reel dans ce jeu).
  epicUnlockCost: { dia: 600 },
  tiers: TIERS,
  dailyMissionCount: 6,
  weeklyMissionCount: 3,
  xpPerDaily: 20,
  xpPerWeekly: 100,
};

/** Cadeau de connexion sur 7 jours (cycle). Aucune pub : tout est offert. */
export const DAILY_REWARDS: readonly DailyRewardDay[] = [
  { day: 1, reward: { wallet: { dia: 30 } } },
  { day: 2, reward: { wallet: { dia: 40 }, tickets: { mine: 1 } } },
  { day: 3, reward: { buffs: [{ id: 'moneyBuff', seconds: 300 }], wallet: { dia: 40 } } },
  { day: 4, reward: { wallet: { dia: 50 }, tickets: { speedRun: 1 } } },
  { day: 5, reward: { draws: [{ pool: 'normal', count: 1 }], wallet: { dia: 50 } } },
  { day: 6, reward: { buffs: [{ id: 'powerBuff', seconds: 300 }], wallet: { dia: 60 } } },
  { day: 7, reward: { draws: [{ pool: 'epic', count: 1 }], wallet: { dia: 100, ruby: 5 } } },
];
